import React from "react"
import { Grid, InputLabel, MenuItem, FormControl, Select } from "@mui/material"
import DiseaseBurdenCharts from "./DiseaseBurdenCharts"
import { get_stats_from_json } from "../../../utils/functions"

const initMalariaData = (periods) => {
  return periods.map((period) => ({
    name: period.replace("_", "-"),
    mal_tot_rep: 0,
    mal_tot_death: 0,
    mal_inp: 0,
  }))
}

const initHivData = (periods) => {
  return periods.map((period) => ({
    name: period.replace("_", "-"),
    curr: 0,
    new: 0,
    return: 0,
    out: 0,
    lost: 0,
    died: 0,
  }))
}

const initTbData = (periods) => {
  return periods.map((period) => ({
    name: period.replace("_", "-"),
    male: {
      new: 0,
      other: 0,
      relapse: 0,
      retreat_ltf: 0,
      retreat_fail: 0,
    },
    female: {
      new: 0,
      other: 0,
      relapse: 0,
      retreat_ltf: 0,
      retreat_fail: 0,
    },
  }))
}

const initHivTbData = (periods) => {
  return periods.map((period) => ({
    name: period.replace("_", "-"),
    male: {
      pos: 0,
      neg: 0,
      unknown: 0,
    },
    female: {
      pos: 0,
      neg: 0,
      unknown: 0,
    },
  }))
}

const calc_stats = (au_geo_json, mode, id) => {
  const hiv_periods = au_geo_json.features[0].properties.hiv.map(
    ({ period }) => period
  )
  const malaria_periods = au_geo_json.features[0].properties.malaria.map(
    ({ period }) => period
  )
  const tb_periods = au_geo_json.features[0].properties.tb.map(
    ({ period }) => period
  )
  let au_sum = {
    neo_au_count: 0,
    mmr_au_count: 0,
    ydr_au_count: 0,
    hyper_18_44: 0,
    hyper_45_above: 0,
    diab_18_44: 0,
    diab_45_above: 0,
    neo: 0,
    mmr: 0,
    ydr: 0,
    malaria: initMalariaData(malaria_periods),
    hiv: initHivData(hiv_periods),
    tb: initTbData(tb_periods),
    hivtb: initHivTbData(tb_periods),
  }

  const filter_property = mode === "districts" ? "district_id" : "cup_id"
  const inside = {
    type: "FeatureLayer",
    features: au_geo_json.features.filter(
      (feature) => feature.properties[filter_property] === id
    ),
  }
  const outside = {
    type: "FeatureLayer",
    features: au_geo_json.features.filter(
      (feature) => feature.properties[filter_property] !== id
    ),
  }

  inside.features.forEach((au) => {
    if (au.properties.neo_death > 0) {
      au_sum.neo += au.properties.neo_death
      au_sum.neo_au_count += 1
    }
    if (au.properties.maternal_mort > 0) {
      au_sum.mmr += au.properties.maternal_mort
      au_sum.mmr_au_count += 1
    }
    if (au.properties.young_delivery > 0) {
      au_sum.ydr += au.properties.young_delivery
      au_sum.ydr_au_count += 1
    }

    au_sum.hyper_18_44 += au.properties.hyper_18_44
    au_sum.hyper_45_above += au.properties.hyper_45_above
    au_sum.diab_18_44 += au.properties.diab_18_44
    au_sum.diab_45_above += au.properties.diab_45_above

    au.properties.malaria.forEach(({ reported, inpatient, death }, index) => {
      au_sum.malaria[index].mal_tot_rep += reported
      au_sum.malaria[index].mal_tot_death += death
      au_sum.malaria[index].mal_inp += inpatient
    })

    au.properties.hiv.forEach((item, index) => {
      au_sum.hiv[index].curr += item.curr
      au_sum.hiv[index].new += item.new
      au_sum.hiv[index].return += item.return
      au_sum.hiv[index].out += item.out
      au_sum.hiv[index].lost += item.lost
      au_sum.hiv[index].died += item.died
    })

    au.properties.tb.forEach((item, index) => {
      ;["male", "female"].forEach((sex) => {
        au_sum.tb[index][sex].new += item[sex].new
        au_sum.tb[index][sex].other += item[sex].other
        au_sum.tb[index][sex].relapse += item[sex].relapse
        au_sum.tb[index][sex].retreat_ltf += item[sex].retreat_ltf
        au_sum.tb[index][sex].retreat_fail += item[sex].retreat_fail
      })
    })

    au.properties.hivtb.forEach((item, index) => {
      ;["male", "female"].forEach((sex) => {
        au_sum.hivtb[index][sex].pos += item[sex].pos
        au_sum.hivtb[index][sex].neg += item[sex].neg
        au_sum.hivtb[index][sex].unknown += item[sex].unknown
      })
    })
  })

  const neo_stats = get_stats_from_json(outside, "neo_death")
  neo_stats.value = au_sum.neo / au_sum.neo_au_count

  const mmr_stats = get_stats_from_json(outside, "maternal_mort")
  mmr_stats.value = au_sum.mmr / au_sum.mmr_au_count

  const ydr_stats = get_stats_from_json(outside, "young_delivery")
  ydr_stats.value = au_sum.ydr / au_sum.ydr_au_count

  const community_health = {
    hyper_18_44: au_sum.hyper_18_44,
    hyper_45_above: au_sum.hyper_45_above,
    diab_18_44: au_sum.diab_18_44,
    diab_45_above: au_sum.diab_45_above,
  }

  return {
    neo_stats,
    mmr_stats,
    ydr_stats,
    community_health,
    malaria: au_sum.malaria,
    hiv: au_sum.hiv,
    tb: au_sum.tb,
    hivtb: au_sum.hivtb,
  }
}

const SelectDisease = ({ disease_selection, set_disease_selection }) => {
  return (
    <FormControl
      variant="standard"
      size="small"
      fullWidth
      style={{ marginBottom: 10 }}
    >
      <InputLabel>Health burden</InputLabel>
      <Select
        value={disease_selection}
        onChange={(event) => {
          set_disease_selection(event.target.value)
        }}
        label="Health burden"
      >
        <MenuItem value={0}>Maternal Health</MenuItem>
        <MenuItem value={1}>Societal</MenuItem>
        <MenuItem value={2}>Malaria</MenuItem>
        <MenuItem value={3}>Community Health</MenuItem>
        <MenuItem value={4}>HIV</MenuItem>
        <MenuItem value={5}>HIV and TB</MenuItem>
        <MenuItem value={6}>TB</MenuItem>
      </Select>
    </FormControl>
  )
}

export default function DiseaseInfo({ au_geo_json, mode, id }) {
  const [disease_selection, set_disease_selection] = React.useState(0)
  if (!au_geo_json || id === undefined || id === null) {
    return null
  }
  const stats = calc_stats(au_geo_json, mode, id)

  return (
    <Grid container sx={{ padding: "10px" }}>
      <div style={{ width: "100%" }}>
        <SelectDisease
          disease_selection={disease_selection}
          set_disease_selection={set_disease_selection}
        />
        <DiseaseBurdenCharts
          disease_selection={disease_selection}
          stats={stats}
        />
      </div>
    </Grid>
  )
}
